'use client';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Calendar, Search } from 'lucide-react';
import { calculateNights } from '@/lib/dateUtils';

/**
 * Pildora de contexto de búsqueda en el Header global.
 *
 * Si hay params en URL (arrival/departure/numAdults) → muestra el contexto:
 *   "📅 19 may → 23 may · 4 noches · 2 huéspedes"
 *
 * Si NO hay params (entrada directa al apto sin búsqueda previa) → fallback:
 *   "🔍 Buscar apartamentos"
 *
 * Click → siempre lleva a /reservas, preservando los params actuales para
 * mantener el contexto de búsqueda del usuario.
 */
const formatDate = (dateStr: string) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
  });

export default function ApartmentSearchBar() {
  const searchParams = useSearchParams();

  const arrival = searchParams.get('arrival');
  const departure = searchParams.get('departure');
  const numAdults = searchParams.get('numAdults');

  const params = new URLSearchParams();
  if (arrival) params.set('arrival', arrival);
  if (departure) params.set('departure', departure);
  if (numAdults) params.set('numAdults', numAdults);

  const href = params.toString() ? `/reservas?${params.toString()}` : '/reservas';

  // Modo contextual: hay búsqueda activa
  const hasContext = !!(arrival && departure && numAdults);

  if (hasContext) {
    const nights = calculateNights(arrival!, departure!);
    const people = parseInt(numAdults!, 10) || 1;

    return (
      <Link
        href={href}
        aria-label={`Modificar búsqueda: ${formatDate(arrival!)} a ${formatDate(departure!)}, ${people} ${people === 1 ? 'huésped' : 'huéspedes'}`}
        className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 sm:px-4 py-2 text-sm font-medium text-gray-800 hover:border-primary-500 hover:text-primary-700 hover:-translate-y-px transition-all duration-200 max-w-full min-w-0"
      >
        <Calendar className="h-4 w-4 text-gray-600 flex-shrink-0" strokeWidth={2.25} />
        <span className="truncate">
          {/* Mobile: compacto */}
          <span className="sm:hidden">
            {formatDate(arrival!)} · {nights}n · {people}
          </span>
          {/* Desktop: legible */}
          <span className="hidden sm:inline">
            {formatDate(arrival!)} → {formatDate(departure!)} · {nights}{' '}
            {nights === 1 ? 'noche' : 'noches'} · {people}{' '}
            {people === 1 ? 'huésped' : 'huéspedes'}
          </span>
        </span>
      </Link>
    );
  }

  // Fallback: sin búsqueda activa
  return (
    <Link
      href={href}
      aria-label="Buscar apartamentos disponibles"
      className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:border-primary-500 hover:text-primary-700 hover:-translate-y-px transition-all duration-200"
    >
      <Search className="h-4 w-4 text-gray-600" strokeWidth={2.25} />
      <span className="whitespace-nowrap">
        Buscar<span className="hidden sm:inline"> apartamentos</span>
      </span>
    </Link>
  );
}
